import { api_configuration } from "../../common";
import api_client_state = api_configuration.types.client_state.alan_api;
import createCollectionQueryFromQuery from "./createCollectionQueryFromQuery";
import updateQueryFromCollectionQueryResult from "./updateQueryFromCollectionQueryResult";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { hasCollectionCreationPermission } from "./permissions";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import updateQueryFromCollection from "../state/updateQueryFromCollection";
import { client_state } from "../state/ClientState";
import { iReference } from "../state/Reference";
import {
	Tcollection_query,
	Tyes__select_entries,
} from "../../../../../common/api_configuration-state/lib/libraries/gui_definition_lib/libraries/model_lib/types/client_request/alan_api";
import { Centries__collection } from "../../../../../common/api_configuration-state/lib/types/client_state/alan_api";
import * as ClientState from "../state/ClientState";
import * as Status from "../Status";
import { awaitCondition } from "../../../../../stream";

async function sendQueryToDataStoreTillCorrectAmountOfEntries(
	query: api_client_state.Cquery__client_state,
	collection_query: Tcollection_query,
	check_limits: boolean
): Promise<any> {
	const collection_query_result = await DatastoreProxy.instance.collectionQuery(collection_query);
	if (query.destroyed || queue.has(query) || throttle_queries.has(query)) {
		return;
	}
	switch (collection_query_result.properties.context.state.name) {
		case "does not exist":
			query.properties.has_result.set("no", {});
			break;
		case "permission denied":
			query.properties.has_result.set("no", {});
			break;
		case "exists": {
			const result = collection_query_result.properties.context.state.node;
			if (check_limits) {
				switch (result.properties.entries.state.name) {
					case "no":
						updateQueryFromCollectionQueryResult(result, query);
						break;
					case "yes": {
						const query_limits = query.properties.definition.ref.properties.query_limits;
						const number_of_entries_limit = collection_query["select entries"][1] as Tyes__select_entries;
						const has_entries = result.properties.entries.state.node;
						switch (has_entries.properties.limit_exceeded.state.name) {
							case "yes":
								if (result.properties.amount_of_entries.value > query_limits.properties.absolute_maximum.value) {
									number_of_entries_limit["limit number of entries"] = query_limits.properties.sample_size.value;
									await sendQueryToDataStoreTillCorrectAmountOfEntries(query, collection_query, false);
								} else if (result.properties.amount_of_entries.value <= query_limits.properties.show_all_limit.value) {
									number_of_entries_limit["limit number of entries"] = query_limits.properties.show_all_limit.value;
									await sendQueryToDataStoreTillCorrectAmountOfEntries(query, collection_query, false);
								} else {
									updateQueryFromCollectionQueryResult(result, query);
								}
								break;
							case "no":
								updateQueryFromCollectionQueryResult(result, query);
								break;
						}
						break;
					}
				}
			} else {
				updateQueryFromCollectionQueryResult(result, query);
			}
		}
	}
}

async function sendQueryToDataStore(
	query: api_client_state.Cquery__client_state,
	collection_query: Tcollection_query
): Promise<void> {
	try {
		await sendQueryToDataStoreTillCorrectAmountOfEntries(query, collection_query, true);
		if (!query.destroyed) {
			query.properties.is_busy_querying.set("no", {
				"request status": ["success", {}],
			});
		}
	} catch (reason) {
		if (!query.destroyed) {
			query.properties.is_busy_querying.set("no", {
				"request status": ["failure", {}],
			});
		}
		throw reason;
	}
}

async function sendQuery(querying: api_client_state.Cyes__is_busy_querying): Promise<void> {
	const query = querying.parent;
	if (querying.destroyed) {
		query.properties.is_busy_querying.set("no", {
			"request status": ["failure", {}],
		});
		return;
	}
	querying.properties.requery_needed.set("no", {});

	let context_node: api_client_state.Cnode | undefined = undefined;
	let force_normal_query_instead_of_candidates_query = false;
	switch (query.properties.use_for.state.name) {
		case "query": {
			const $ = query.properties.use_for.state.node;
			context_node = $.inferences.node_context().properties.switch_.switch({
				current: () => $.inferences.node(),
				root: () => client_state.properties.root,
			});
			break;
		}
		case "candidates": {
			const $ = query.properties.use_for.state.node;
			const reference = $.inferences.reference_property().parent;
			const entry = reference.parent.properties.is_key.switch<undefined | Centries__collection>({
				no: undefined,
				yes: ($) => $.inferences.entry(),
			});
			const collection = await awaitCondition(iReference(reference).collectionContext, Status.valueOrFailed);
			if (collection !== Status.Failed) {
				entriesAreEditable($, collection);
				if (entry && entry.destroyed === false) {
					switch (entry.component_root.properties.location.state.name) {
						case "local": {
							force_normal_query_instead_of_candidates_query = true;
							context_node = collection.parent.parent.parent;
							break;
						}
						case "remote": {
							context_node = entry.parent.parent.parent.parent;
							break;
						}
					}
				} else {
					context_node = collection.parent.parent.parent;
				}
			}
			break;
		}
	}

	if (query.destroyed) return;
	if (querying.destroyed) {
		query.properties.is_busy_querying.set("no", {
			"request status": ["failure", {}],
		});
		return;
	}
	if (context_node && context_node.destroyed === false) {
		await context_node.properties.location.switch({
			remote: async () =>
				await sendQueryToDataStore(
					query,
					createCollectionQueryFromQuery(
						createIdPathBuilderFromNode(context_node!),
						query,
						queryLimit(query),
						force_normal_query_instead_of_candidates_query
					)
				),
			local: async () => {
				query.properties.is_busy_querying.set("no", {
					"request status": ["success", {}],
				});
			},
		});
		if (query.destroyed) return;
		updateQueryFromCollection(query);
	} else {
		query.properties.has_result.set("no", {});
		query.properties.is_busy_querying.set("no", {
			"request status": ["failure", {}],
		});
	}
}

const queue = new Set<api_client_state.Cquery__client_state>();
async function queueQuery(query: api_client_state.Cquery__client_state): Promise<void> {
	if (!queue.has(query)) {
		queue.add(query);
		await new Promise<void>((resolve) => {
			ClientState.onTransactionEnds(() => {
				resolve();
			});
		});
		queue.delete(query);
		if (query.destroyed) return;
		const busy = query.properties.is_busy_querying.set("yes", {
			"requery needed": "no",
		});
		await sendQuery(busy);
	}
}

function isBusy(query: api_client_state.Cquery__client_state): boolean {
	switch (query.properties.is_busy_querying.state.name) {
		case "no":
			return false;
		case "yes":
			return true;
	}
}

async function waitForQuery(query: api_client_state.Cquery__client_state): Promise<void> {
	if (query.destroyed === false && isBusy(query)) {
		return new Promise((resolve) => {
			query.properties.is_busy_querying.subscribe({
				on_update: (_, subscription) => {
					if (!isBusy(query)) {
						subscription.unsubscribe();
						resolve();
					}
				},
			});
		});
	} else {
		return Promise.resolve();
	}
}

export function query(query: api_client_state.Cquery__client_state): void {
	queueQuery(query);
}

const throttle_queries = new Set<api_client_state.Cquery__client_state>();
export function execute(query: api_client_state.Cquery__client_state): void {
	if (query.destroyed || throttle_queries.has(query)) {
		return;
	}
	switch (query.properties.is_busy_querying.state.name) {
		case "yes": {
			const busy = query.properties.is_busy_querying.state.node;
			switch (busy.properties.requery_needed.state.name) {
				case "no": {
					busy.properties.requery_needed.set("yes", {});
					throttle_queries.add(query);
					setTimeout(async () => {
						await waitForQuery(query);
						throttle_queries.delete(query);
						execute(query);
					}, 200);
					break;
				}
				case "yes":
					break;
			}
			break;
		}
		case "no": {
			queueQuery(query);
		}
	}
}

function setEntriesAreEditable(query: api_client_state.Cquery__client_state) {
	query.properties.entries_are_editable.set("yes", {
		"is importing": ["no", {}],
		"can create entry": [
			"yes",
			{
				in: ["candidates", {}],
			},
		],
	});
}

function setEntriesAreNotEditable(query: api_client_state.Cquery__client_state) {
	query.properties.entries_are_editable.set("no", {});
}

function entriesAreEditable(
	candidates_query: api_client_state.Ccandidates__use_for__query,
	collection: api_client_state.Ccollection
) {
	const query = candidates_query.parent;
	query.properties.definition.ref.properties.context.state.node.output
		.selected_context()
		.input.application_node()
		.input.value_source()
		.switch({
			derived: () => setEntriesAreNotEditable(query),
			base: () => {
				const collection_name = collection.parent.parent.key.entry;
				const node = collection.parent.parent.parent;
				node.properties.location.switch({
					remote: () =>
						hasCollectionCreationPermission(createIdPathBuilderFromNode(node), collection_name, (editable) => {
							if (query.destroyed) {
								return;
							}
							if (editable) {
								setEntriesAreEditable(query);
							} else {
								setEntriesAreNotEditable(query);
							}
						}),
					local: () => setEntriesAreEditable(query),
				});
			},
		});
}

function walkQueries(node: api_client_state.Cnode, on_query: (query: api_client_state.Cquery__client_state) => void) {
	node.properties.queries.forEach(($query) => on_query($query.properties.query));
	node.properties.attributes.forEach(($attribute) => {
		$attribute.properties.type.switch({
			operation: undefined,
			property: ($property) => {
				$property.properties.type.switch({
					file: undefined,
					number: undefined,
					text: undefined,
					group: ($) => walkQueries($.properties.node, on_query),
					"state group": ($) => {
						$.properties.states.forEach(($) => walkQueries($.properties.node, on_query));
					},
					collection: ($) => {
						$.properties.entries.forEach(($) => walkQueries($.properties.node, on_query));
					},
				});
			},
		});
	});
}

export function queriesForCollection($entry: api_client_state.Centries__collection): void {
	walkQueries(client_state.properties.root, ($query) => {
		const selected_context = $query.properties.definition.ref.properties.context.state.node.output
			.selected_context()
			.input.application_node();
		const entry_context = $entry.properties.node.input.node_type();
		if (selected_context === entry_context) {
			execute($query);
		}
	});
}

export function candidates(reference: api_client_state.Cyes__navigable): void {
	for (const query of reference.properties.queries.values()) {
		switch (query.properties.query.properties.delay_query.state.name) {
			case "no": {
				execute(query.properties.query);
				break;
			}
			case "yes":
				break;
		}
	}
}

function queryLimit(query: api_client_state.Cquery__client_state) {
	const query_limits = query.properties.definition.ref.properties.query_limits;
	return query.properties.show_all.switch({
		no: query_limits.properties.sample_size.value,
		yes: query_limits.properties.absolute_maximum.value,
	});
}

export function queriesForCollectionFromNode(
	node: api_client_state.Cnode,
	collection: api_client_state.Ccollection
): void {
	Array.from(node.properties.queries).map((query) => {
		if (
			query[1].properties.query.properties.definition.ref.properties.context.state.node.output
				.selected_context()
				.input.application_node() === collection.inferences.collection_type().properties.node
		) {
			execute(query[1].properties.query);
		}
	});
}

export function stopDelayingQuery(query: api_client_state.Cquery__client_state): void {
	switch (query.properties.delay_query.state.name) {
		case "no":
			break;
		case "yes": {
			query.properties.delay_query.set("no", {});
			execute(query);
			break;
		}
		default:
			unexpectedState(query.properties.delay_query.state);
	}
}
